import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { 
  collection, query, where, onSnapshot, doc, getDoc, updateDoc, setDoc, addDoc, Timestamp
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  IoTimerOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline, IoCreateOutline, IoPeopleOutline, IoCalculatorOutline, IoDocumentTextOutline, IoPrintOutline, IoArrowBackOutline
} from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import { calculateSATProgressiveISR, postPayrollDevengoAsiento, postPayrollPagoAsiento } from '../utils/accountingUtils';
import './HomeScreen.css';

function AdminTimesheetApprovalScreen() {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSaaS = location.pathname.includes('/superuser/hr');
  const activeCondominioId = isSaaS ? 'SAAS' : userData?.condominioId;

  const [empleados, setEmpleados] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [asistencias, setAsistencias] = useState([]);
  const [metasCumplidas, setMetasCumplidas] = useState([]);

  // Period state
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1); // 1-12
  const [quincena, setQuincena] = useState(1); // 1 or 2
  const [semanaNum, setSemanaNum] = useState(1); // 1 to 5
  const [catorcenaNum, setCatorcenaNum] = useState(1); // 1 or 2

  // Calculation parameters
  const [policy, setPolicy] = useState('completa'); // 'completa' or 'horas'
  const [processing, setProcessing] = useState(false);
  const [payInfo, setPayInfo] = useState({ 
    sueldoQuincena: 0, 
    workedDaysCount: 0, 
    activeDutyDays: 0, 
    faltasCount: 0, 
    horasTrabajadas: 0, 
    neto: 0, 
    bonoTotal: 0, 
    deduccionTotal: 0, 
    isrRetenido: 0, 
    imssRetenido: 0, 
    imssPatronal: 0, 
    isn: 0 
  });
  const [recibos, setRecibos] = useState([]);

  // Fetch receipts
  useEffect(() => {
    if (!selectedEmp?.usuarioId || !activeCondominioId) {
      setRecibos([]);
      return;
    }
    const q = query(
      collection(db, "nominas_recibos"),
      where("trabajadorId", "==", selectedEmp.usuarioId),
      where("condominioId", "==", activeCondominioId)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.toMillis ? b.createdAt.toMillis() : 0) - (a.createdAt?.toMillis ? a.createdAt.toMillis() : 0));
      setRecibos(list);
    });
    return () => unsubscribe();
  }, [selectedEmp, activeCondominioId]);
  // Edit record modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDateStr, setEditDateStr] = useState('');
  const [editCheckIn, setEditCheckIn] = useState('');
  const [editCheckOut, setEditCheckOut] = useState('');
  const [editJustificacion, setEditJustificacion] = useState('');
  const [showDispersionModal, setShowDispersionModal] = useState(false);
  const [selectedRecibo, setSelectedRecibo] = useState(null);
  const [dispersionBanco, setDispersionBanco] = useState('102.01');
  const [bancoSaldo, setBancoSaldo] = useState(120000);
  const [savingEdit, setSavingEdit] = useState(false);

  // Fetch employees
  useEffect(() => {
    if (!activeCondominioId) return;

    const q = query(
      collection(db, "empleados"),
      where("condominioId", "==", activeCondominioId)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEmpleados(list);
      if (list.length > 0 && !selectedEmp) {
        setSelectedEmp(list[0]);
      }
    });
    return () => unsubscribe();
  }, [activeCondominioId]);

  const getPeriodDateRange = () => {
    const pPago = selectedEmp?.periodoPago || 'Quincenal';
    const lastDayOfMonth = new Date(anio, mes, 0).getDate();

    if (pPago === 'Semanal') {
      const startDay = (semanaNum - 1) * 7 + 1;
      const endDay = semanaNum === 5 ? lastDayOfMonth : Math.min(lastDayOfMonth, semanaNum * 7);
      return { startDay, endDay };
    } else if (pPago === 'Catorcenal') {
      const startDay = catorcenaNum === 1 ? 1 : 15;
      const endDay = catorcenaNum === 1 ? 14 : lastDayOfMonth;
      return { startDay, endDay };
    } else if (pPago === 'Mensual') {
      return { startDay: 1, endDay: lastDayOfMonth };
    } else {
      const startDay = quincena === 1 ? 1 : 16;
      const endDay = quincena === 1 ? 15 : lastDayOfMonth;
      return { startDay, endDay };
    }
  };

  // Fetch asistencias & goals for selected employee and period
  useEffect(() => {
    if (!selectedEmp?.usuarioId) {
      setAsistencias([]);
      setMetasCumplidas([]);
      return;
    }

    const { startDay, endDay } = getPeriodDateRange();
    const pad = (n) => String(n).padStart(2, '0');
    const startStr = `${anio}-${pad(mes)}-${pad(startDay)}`;
    const endStr = `${anio}-${pad(mes)}-${pad(endDay)}`;

    // Assistances query
    const qAsis = query(
      collection(db, "asistencia_trabajadores"),
      where("trabajadorId", "==", selectedEmp.usuarioId),
      where("fecha", ">=", startStr),
      where("fecha", "<=", endStr)
    );
    const unsubAsis = onSnapshot(qAsis, (snap) => {
      setAsistencias(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // Metas query
    const qMetas = query(
      collection(db, "trabajadores_metas"),
      where("trabajadorId", "==", selectedEmp.usuarioId),
      where("estado", "==", "cumplido")
    );
    const unsubMetas = onSnapshot(qMetas, (snap) => {
      setMetasCumplidas(snap.docs.map(d => d.data()));
    });

    return () => {
      unsubAsis();
      unsubMetas();
    };
  }, [selectedEmp, anio, mes, quincena, semanaNum, catorcenaNum]);

  // Generate period days list
  const getPeriodDays = () => {
    const list = [];
    const { startDay, endDay } = getPeriodDateRange();
    
    for (let d = startDay; d <= endDay; d++) {
      const date = new Date(anio, mes - 1, d);
      const pad = (n) => String(n).padStart(2, '0');
      const dateStr = `${anio}-${pad(mes)}-${pad(d)}`;
      list.push({
        dateStr,
        dayName: date.toLocaleDateString('es-MX', { weekday: 'long' }),
        dayNum: d
      });
    }
    return list;
  };

  const periodDays = getPeriodDays();

  // Find attendance record for a specific day
  const getRecordForDay = (dateStr) => {
    return asistencias.find(a => a.fecha === dateStr);
  };

  // Open edit modal
  const handleOpenEdit = (dateStr) => {
    const record = getRecordForDay(dateStr);
    setEditDateStr(dateStr);
    if (record) {
      setEditCheckIn(record.checkIn ? new Date(record.checkIn.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '');
      setEditCheckOut(record.checkOut ? new Date(record.checkOut.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '');
      setEditJustificacion(record.justificacion || '');
    } else {
      setEditCheckIn('09:00');
      setEditCheckOut('18:00');
      setEditJustificacion('Olvido de registro de asistencia');
    }
    setShowEditModal(true);
  };

  // Save manual attendance adjustment
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedEmp) return;

    setSavingEdit(true);
    try {
      const record = getRecordForDay(editDateStr);
      
      const [hIn, mIn] = editCheckIn.split(':').map(Number);
      const [hOut, mOut] = editCheckOut.split(':').map(Number);
      
      const inDate = new Date(anio, mes - 1, Number(editDateStr.split('-')[2]), hIn, mIn);
      const outDate = new Date(anio, mes - 1, Number(editDateStr.split('-')[2]), hOut, mOut);

      const payload = {
        trabajadorId: selectedEmp.usuarioId,
        trabajadorNombre: `${selectedEmp.nombre} ${selectedEmp.apellidos}`,
        condominioId: userData.condominioId,
        fecha: editDateStr,
        checkIn: Timestamp.fromDate(inDate),
        checkOut: Timestamp.fromDate(outDate),
        justificacion: editJustificacion,
        estado: 'completado',
        ajusteManual: true,
        ajustadoPor: userData?.nombre || 'Administrador'
      };

      if (record) {
        await updateDoc(doc(db, "asistencia_trabajadores", record.id), payload);
      } else {
        await addDoc(collection(db, "asistencia_trabajadores"), payload);
      }
      setShowEditModal(false);
    } catch (error) {
      console.error(error);
      alert("Error al guardar el ajuste.");
    } finally {
      setSavingEdit(false);
    }
  };

  // Calculations
  useEffect(() => {
    if (!selectedEmp) return;

    const runCalc = async () => {
      const pPago = selectedEmp.periodoPago || 'Quincenal';
      let baseSalary = 0;
      if (pPago === 'Semanal') {
        baseSalary = (selectedEmp.salarioBruto || 0) / 4;
      } else if (pPago === 'Catorcenal') {
        baseSalary = (selectedEmp.salarioBruto || 0) / 2.15;
      } else if (pPago === 'Mensual') {
        baseSalary = (selectedEmp.salarioBruto || 0);
      } else {
        baseSalary = (selectedEmp.salarioBruto || 0) / 2; // Quincenal
      }

      let workedDaysCount = 0;
      let totalWorkedHours = 0;
      let activeDutyDays = 0;

      periodDays.forEach(day => {
        // Check if it's a working day
        const key = Object.keys(selectedEmp.horariosDetalle || {}).find(k => k.toLowerCase() === day.dayName.toLowerCase());
        const isWorkingDay = key ? selectedEmp.horariosDetalle[key].activo : (selectedEmp.diasLaborales ? selectedEmp.diasLaborales.some(d => d.toLowerCase() === day.dayName.toLowerCase()) : true);
        
        if (isWorkingDay) {
          activeDutyDays++;
          const rec = getRecordForDay(day.dateStr);
          if (rec && rec.checkIn && rec.checkOut) {
            workedDaysCount++;
            const diffMs = rec.checkOut.toMillis() - rec.checkIn.toMillis();
            totalWorkedHours += diffMs / (1000 * 60 * 60);
          }
        }
      });

      const faltasCount = Math.max(0, activeDutyDays - workedDaysCount);
      
      // Deductions
      let deduccionTotal = 0;
      if (policy === 'horas') {
        // Calculate exact missing hours vs standard 8 hours per shift
        const expectedHours = activeDutyDays * 8;
        const missingHours = Math.max(0, expectedHours - totalWorkedHours);
        const hourlyRate = baseSalary / expectedHours;
        deduccionTotal = missingHours * hourlyRate;
      } else {
        // Fixed policy: deduct daily rate for unjustified absences
        const dailyRate = baseSalary / activeDutyDays;
        deduccionTotal = faltasCount * dailyRate;
      }

      const bonoTotal = metasCumplidas.reduce((acc, curr) => acc + (curr.bono || 0), 0);
      
      // Calculate progressive ISR from SAT tables
      const isrRetenido = await calculateSATProgressiveISR(baseSalary + bonoTotal, pPago);
      
      // IMSS Retenido Empleado: 2.5% of gross
      const imssRetenido = parseFloat(((baseSalary + bonoTotal) * 0.025).toFixed(2));
      
      // IMSS Patronal: 15% of gross
      const imssPatronal = parseFloat(((baseSalary + bonoTotal) * 0.15).toFixed(2));

      // ISN (Impuesto sobre nóminas): 3% of gross
      const isn = parseFloat(((baseSalary + bonoTotal) * 0.03).toFixed(2));

      const neto = Math.max(0, baseSalary - parseFloat(deduccionTotal) + bonoTotal - isrRetenido - imssRetenido);

      setPayInfo({
        sueldoQuincena: baseSalary,
        workedDaysCount,
        activeDutyDays,
        faltasCount,
        horasTrabajadas: totalWorkedHours.toFixed(1),
        bonoTotal,
        deduccionTotal: parseFloat(deduccionTotal).toFixed(2),
        neto: neto.toFixed(2),
        isrRetenido,
        imssRetenido,
        imssPatronal,
        isn
      });
    };
    runCalc();
  }, [selectedEmp, asistencias, metasCumplidas, policy, anio, mes, quincena, semanaNum, catorcenaNum]);

  // Process & Stamp payroll (FacturAPI Mock)
  const handleProcessPayroll = async () => {
    if (!selectedEmp) return;
    if (!window.confirm(`¿Confirmas que deseas timbrar la nómina del periodo para ${selectedEmp.nombre}? Esto generará el recibo oficial de forma definitiva.`)) return;

    setProcessing(true);
    try {
      // FacturAPI Mock Stamping
      const facturapiReceiptId = `r_recibo_${Math.random().toString(36).substring(2, 9)}`;

      const pad = (n) => String(n).padStart(2, '0');
      const mesNombre = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][mes - 1];
      
      let periodoStr = '';
      if (selectedEmp.periodoPago === 'Semanal') {
        periodoStr = `Semana ${semanaNum} - ${mesNombre} ${anio}`;
      } else if (selectedEmp.periodoPago === 'Catorcenal') {
        periodoStr = `Catorcena ${catorcenaNum} - ${mesNombre} ${anio}`;
      } else if (selectedEmp.periodoPago === 'Mensual') {
        periodoStr = `Mes Completo - ${mesNombre} ${anio}`;
      } else {
        periodoStr = `Quincena ${quincena} - ${mesNombre} ${anio}`;
      }

      const docRef = await addDoc(collection(db, "nominas_recibos"), {
        trabajadorId: selectedEmp.usuarioId,
        trabajadorNombre: `${selectedEmp.nombre} ${selectedEmp.apellidos}`,
        periodo: periodoStr,
        fechaPago: new Date().toISOString().split('T')[0],
        sueldoBruto: Number(payInfo.sueldoQuincena) + Number(payInfo.bonoTotal),
        deducciones: Number(payInfo.deduccionTotal),
        bonos: Number(payInfo.bonoTotal),
        netoPagado: Number(payInfo.neto),
        horasTrabajadas: Number(payInfo.horasTrabajadas),
        facturapiReceiptId,
        condominioId: activeCondominioId,
        estatus: 'Timbrado',
        createdAt: new Date()
      });

      // Post Devengo Ledger entry
      await postPayrollDevengoAsiento(activeCondominioId, docRef.id, {
        trabajadorNombre: `${selectedEmp.nombre} ${selectedEmp.apellidos}`,
        periodo: periodoStr,
        sueldoBruto: Number(payInfo.sueldoQuincena) + Number(payInfo.bonoTotal),
        netoPagado: Number(payInfo.neto),
        isrRetenido: Number(payInfo.isrRetenido),
        imssRetenido: Number(payInfo.imssRetenido),
        imssPatronal: Number(payInfo.imssPatronal),
        isn: Number(payInfo.isn)
      });

      alert("¡Nómina procesada y timbrada exitosamente en FacturAPI! El recibo de pago ya está disponible en la cuenta del trabajador y se registró el asiento contable.");
    } catch (error) {
      console.error(error);
      alert("Error al procesar la nómina.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadLayout = (recibo) => {
    const clabe = selectedEmp?.clabe || 'No configurada';
    const banco = selectedEmp?.banco || 'No configurado';
    const amount = recibo.netoPagado || 0;
    const beneficiary = recibo.trabajadorNombre || 'Trabajador';
    const concepto = `Pago Nomina ${recibo.periodo}`;

    const headers = ['Banco Destino', 'CLABE', 'Monto (MXN)', 'Beneficiario', 'Concepto de Pago'];
    const rows = [
      [banco, clabe, amount, beneficiary, concepto]
    ];

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Layout_Dispersion_Nomina_${recibo.id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmDispersion = async () => {
    if (!selectedRecibo || !activeCondominioId) return;
    try {
      await updateDoc(doc(db, "nominas_recibos", selectedRecibo.id), {
        estatus: 'Pagado',
        fechaDispersado: new Date().toISOString().split('T')[0],
        bancoSalida: dispersionBanco
      });

      await postPayrollPagoAsiento(activeCondominioId, selectedRecibo.id, {
        trabajadorNombre: selectedRecibo.trabajadorNombre,
        periodo: selectedRecibo.periodo,
        netoPagado: Number(selectedRecibo.netoPagado)
      });

      alert("La dispersión ha sido ejecutada con éxito. El recibo se marcó como Pagado y se generó el asiento contable de salida de bancos.");
      setShowDispersionModal(false);
      setSelectedRecibo(null);
    } catch (e) {
      console.error("Error executing payroll dispersion:", e);
      alert("Error al ejecutar la dispersión bancaria.");
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/nominas-dashboard" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          <style>{`
            .ks-responsive-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 20px;
            }
            .ks-payroll-breakdown {
              display: grid;
              grid-template-columns: 1fr;
              gap: 20px;
            }
            @media (min-width: 768px) {
              .ks-responsive-grid {
                grid-template-columns: 280px 1fr;
                gap: 30px;
              }
            }
            @media (min-width: 600px) {
              .ks-payroll-breakdown {
                grid-template-columns: 1fr 1fr;
                gap: 30px;
              }
            }
          `}</style>

          {/* Header Hero */}
          <div className="muro-header-stitch" style={{ marginBottom: '20px' }}>
            <div className="muro-hero-card" style={{ 
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '15px 20px',
              minHeight: 'auto',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', gap: '15px', zIndex: 2 }}>
                <button 
                  onClick={() => navigate(isSaaS ? '/superuser/hr/dashboard' : '/nominas-dashboard')} 
                  className="usuarios-back-btn ks-back-btn-reco glass" 
                  title="Volver" 
                  style={{ margin: 0, position: 'relative', flexShrink: 0 }}
                >
                  <IoArrowBackOutline size={24} />
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                  <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.2, color: 'white', whiteSpace: 'normal', wordBreak: 'break-word' }}>Aprobación y Pre-Nómina</h1>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.8)', whiteSpace: 'normal', wordBreak: 'break-word' }}>Valida las horas trabajadas, aprueba los timesheets e inicia la dispersión fiscal SAT</p>
                </div>
              </div>
              <IoTimerOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
            {/* Filters */}
            <div style={{ background: 'white', borderRadius: '16px', padding: '16px', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>Año</label>
                <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>Mes</label>
                <select value={mes} onChange={e => setMes(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                  {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, idx) => (
                    <option key={idx} value={idx + 1}>{m}</option>
                  ))}
                </select>
              </div>
              {selectedEmp && (
                <div>
                  {selectedEmp.periodoPago === 'Semanal' && (
                    <>
                      <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>Semana del Mes</label>
                      <select value={semanaNum} onChange={e => setSemanaNum(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                        <option value={1}>Semana 1 (Días 1 al 7)</option>
                        <option value={2}>Semana 2 (Días 8 al 14)</option>
                        <option value={3}>Semana 3 (Días 15 al 21)</option>
                        <option value={4}>Semana 4 (Días 22 al 28)</option>
                        <option value={5}>Semana 5 (Días 29 al fin)</option>
                      </select>
                    </>
                  )}
                  {selectedEmp.periodoPago === 'Catorcenal' && (
                    <>
                      <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>Catorcena</label>
                      <select value={catorcenaNum} onChange={e => setCatorcenaNum(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                        <option value={1}>Periodo 1 (Días 1 al 14)</option>
                        <option value={2}>Periodo 2 (Días 15 al fin)</option>
                      </select>
                    </>
                  )}
                  {selectedEmp.periodoPago === 'Mensual' && (
                    <>
                      <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>Periodo Completo</label>
                      <span style={{ fontSize: '0.9rem', color: '#475569', display: 'inline-block', paddingTop: '8px', fontWeight: 'bold' }}>Mes Completo (1 al fin)</span>
                    </>
                  )}
                  {(selectedEmp.periodoPago === 'Quincenal' || !selectedEmp.periodoPago) && (
                    <>
                      <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '4px' }}>Quincena</label>
                      <select value={quincena} onChange={e => setQuincena(Number(e.target.value))} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                        <option value={1}>1ra Quincena (1 al 15)</option>
                        <option value={2}>2da Quincena (16 al fin)</option>
                      </select>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="ks-responsive-grid">
              
              {/* Sidebar: Empleados */}
              <div style={{ background: 'white', borderRadius: '16px', padding: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: 'fit-content' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b' }}>
                  <IoPeopleOutline size={18} />
                  <span>Personal</span>
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {empleados.map(emp => {
                    const active = selectedEmp?.id === emp.id;
                    return (
                      <button
                        key={emp.id}
                        onClick={() => setSelectedEmp(emp)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px',
                          borderRadius: '12px',
                          border: active ? '1px solid #0369a1' : '1px solid #f1f5f9',
                          background: active ? '#f0f9ff' : 'white',
                          color: active ? '#0369a1' : '#475569',
                          fontWeight: active ? '700' : '500',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px'
                        }}
                      >
                        <span style={{ fontSize: '0.9rem' }}>{emp.nombre} {emp.apellidos}</span>
                        <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>{emp.puesto}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Main Area */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', minWidth: 0 }}>
                
                {/* 1. Timesheet Approval Grid */}
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', color: '#1e293b' }}>Registro de Horas (Timesheet)</h3>
                  
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                          <th style={{ padding: '12px' }}>Día / Fecha</th>
                          <th style={{ padding: '12px' }}>Check-In</th>
                          <th style={{ padding: '12px' }}>Check-Out</th>
                          <th style={{ padding: '12px' }}>Horas</th>
                          <th style={{ padding: '12px' }}>Estado / Justificación</th>
                          <th style={{ padding: '12px', textAlign: 'center' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {periodDays.map(day => {
                          const rec = getRecordForDay(day.dateStr);
                          const key = Object.keys(selectedEmp?.horariosDetalle || {}).find(k => k.toLowerCase() === day.dayName.toLowerCase());
                          const isWorkingDay = key ? selectedEmp.horariosDetalle[key].activo : (selectedEmp?.diasLaborales ? selectedEmp.diasLaborales.some(d => d.toLowerCase() === day.dayName.toLowerCase()) : true);
                          
                          let displayIn = '--:--';
                          let displayOut = '--:--';
                          let hours = 0;
                          
                          if (rec) {
                            displayIn = rec.checkIn ? new Date(rec.checkIn.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--';
                            displayOut = rec.checkOut ? new Date(rec.checkOut.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--';
                            if (rec.checkIn && rec.checkOut) {
                              hours = (rec.checkOut.toMillis() - rec.checkIn.toMillis()) / (1000 * 60 * 60);
                            }
                          }

                          return (
                            <tr key={day.dateStr} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '12px' }}>
                                <strong style={{ textTransform: 'capitalize' }}>{day.dayName.substring(0, 3)}</strong> {day.dayNum}
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{day.dateStr}</div>
                              </td>
                              <td style={{ padding: '12px' }}>{displayIn}</td>
                              <td style={{ padding: '12px' }}>{displayOut}</td>
                              <td style={{ padding: '12px' }}>{hours > 0 ? `${hours.toFixed(1)} hrs` : '-'}</td>
                              <td style={{ padding: '12px' }}>
                                {!isWorkingDay ? (
                                  <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Descanso</span>
                                ) : rec ? (
                                  <div>
                                    <span style={{ color: '#22c55e', fontWeight: 'bold' }}>Presente</span>
                                    {rec.justificacion && <div style={{ fontSize: '0.75rem', color: '#d97706', fontStyle: 'italic' }}>✍️ {rec.justificacion}</div>}
                                  </div>
                                ) : (
                                  <span style={{ color: '#ef4444', fontWeight: 'bold' }}>Falta</span>
                                )}
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                {isWorkingDay && (
                                  <button 
                                    onClick={() => handleOpenEdit(day.dateStr)}
                                    style={{ background: 'transparent', border: 'none', color: '#0284c7', cursor: 'pointer' }}
                                  >
                                    <IoCreateOutline size={20} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 2. Payroll Processing Panel */}
                {selectedEmp && (
                  <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: '0 0 20px 0', fontSize: '1.2rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IoCalculatorOutline size={22} style={{ color: '#0369a1' }} />
                      <span>Procesamiento de Nómina del Periodo</span>
                    </h3>

                    {/* Calculation Policy Selection */}
                    <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', marginBottom: '20px' }}>
                      <strong style={{ fontSize: '0.85rem', color: '#475569', display: 'block', marginBottom: '8px' }}>Política de Deducción:</strong>
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                          <input type="radio" checked={policy === 'completa'} onChange={() => setPolicy('completa')} />
                          <span>Pagar Completa (Descontar solo Faltas)</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                          <input type="radio" checked={policy === 'horas'} onChange={() => setPolicy('horas')} />
                          <span>Pago Proporcional por Horas Trabajadas (Exacto)</span>
                        </label>
                      </div>
                    </div>

                    {/* Math breakdown */}
                    <div className="ks-payroll-breakdown" style={{ marginBottom: '24px' }}>
                      <div>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#475569' }}>Resumen del Periodo</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.9rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Días Laborables:</span>
                            <strong>{payInfo.activeDutyDays} días</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Días Asistidos:</span>
                            <strong style={{ color: '#16a34a' }}>{payInfo.workedDaysCount} días</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Horas Trabajadas:</span>
                            <strong>{payInfo.horasTrabajadas} hrs</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Faltas Injustificadas:</span>
                            <strong style={{ color: payInfo.faltasCount > 0 ? '#dc2626' : '#64748b' }}>{payInfo.faltasCount} faltas</strong>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#475569' }}>Desglose de Pago</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.9rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Sueldo Bruto Quincenal:</span>
                            <strong>${payInfo.sueldoQuincena.toFixed(2)}</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Bonificaciones (Metas):</span>
                            <strong style={{ color: '#16a34a' }}>+${payInfo.bonoTotal.toFixed(2)}</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Deducciones (Faltas/Horas):</span>
                            <strong style={{ color: '#dc2626' }}>-${payInfo.deduccionTotal}</strong>
                          </div>
                          <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '8px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem' }}>
                            <strong>Neto a Pagar:</strong>
                            <strong style={{ color: '#0369a1' }}>${payInfo.neto} MXN</strong>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                      <button 
                        onClick={handleProcessPayroll} 
                        disabled={processing}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '12px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        <IoDocumentTextOutline size={20} />
                        <span>{processing ? 'Timbrando CFDI...' : 'Procesar y Timbrar Nómina SAT'}</span>
                      </button>
                    </div>

                  </div>
                )}

                {/* 3. Stamped Receipts & Dispersion History */}
                {selectedEmp && recibos.length > 0 && (
                  <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IoDocumentTextOutline size={22} style={{ color: '#7e22ce' }} />
                      <span>Recibos Emitidos y Dispersión Bancaria</span>
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {recibos.map(rec => (
                        <div key={rec.id} style={{
                          padding: '16px',
                          borderRadius: '12px',
                          border: '1px solid #f1f5f9',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: rec.estatus === 'Pagado' ? '#f0fdf4' : '#fffbeb'
                        }}>
                          <div>
                            <strong style={{ fontSize: '0.95rem', color: '#1e293b' }}>{rec.periodo}</strong>
                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                              Neto: <strong>${rec.netoPagado?.toFixed(2)} MXN</strong> | Estado: <span style={{
                                fontWeight: 'bold',
                                color: rec.estatus === 'Pagado' ? '#16a34a' : '#d97706'
                              }}>{rec.estatus}</span>
                            </div>
                            {rec.fechaDispersado && (
                              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                Dispersado el: {rec.fechaDispersado} ({rec.bancoSalida})
                              </div>
                            )}
                          </div>
                          
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {rec.estatus !== 'Pagado' && (
                              <>
                                <button
                                  onClick={() => handleDownloadLayout(rec)}
                                  style={{
                                    background: 'white',
                                    border: '1px solid #cbd5e1',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    color: '#475569'
                                  }}
                                >
                                  📥 Descargar Layout
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedRecibo(rec);
                                    setShowDispersionModal(true);
                                  }}
                                  style={{
                                    background: '#7e22ce',
                                    border: 'none',
                                    color: 'white',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                  }}
                                >
                                  💳 Dispersar SPEI
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>

            </div>
          </div>
        </div>

        {/* Modal: Ajustar asistencia */}
        {showEditModal && (
          <div className="notifications-overlay" onClick={() => setShowEditModal(false)}>
            <div className="notifications-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
              <div className="notifications-modal-header">
                <h2>Justificar Asistencia</h2>
                <button className="close-modal-btn" onClick={() => setShowEditModal(false)}>X</button>
              </div>
              <form onSubmit={handleSaveEdit} style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px' }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}><strong>Fecha:</strong> {editDateStr}</p>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Hora Check-In</label>
                    <input 
                      type="time" 
                      value={editCheckIn} 
                      onChange={e => setEditCheckIn(e.target.value)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                      required
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Hora Check-Out</label>
                    <input 
                      type="time" 
                      value={editCheckOut} 
                      onChange={e => setEditCheckOut(e.target.value)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                      required
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '6px' }}>Motivo de Justificación *</label>
                  <input 
                    type="text" 
                    value={editJustificacion} 
                    onChange={e => setEditJustificacion(e.target.value)} 
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} 
                    placeholder="Ej. Olvido de registro, falla técnica"
                    required 
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={savingEdit}
                  style={{
                    background: '#0369a1',
                    color: 'white',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '10px'
                  }}
                >
                  {savingEdit ? 'Guardando...' : 'Aplicar Ajuste Manual'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Modal: Confirmación de Dispersión */}
        {showDispersionModal && selectedRecibo && (
          <div className="notifications-overlay" onClick={() => setShowDispersionModal(false)}>
            <div className="notifications-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
              <div className="notifications-modal-header">
                <h2>Dispersión Bancaria (SPEI)</h2>
                <button className="close-modal-btn" onClick={() => setShowDispersionModal(false)}>X</button>
              </div>
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                  Vas a transferir los fondos correspondientes a la nómina de <strong>{selectedRecibo.trabajadorNombre}</strong> para el periodo <strong>{selectedRecibo.periodo}</strong>.
                </p>

                <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85rem' }}>
                    <span style={{ color: '#64748b' }}>Cuenta Origen:</span>
                    <strong>102.01 - Bancos Nacionales (Belvo)</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85rem' }}>
                    <span style={{ color: '#64748b' }}>Saldo Disponible:</span>
                    <strong style={{ color: '#16a34a' }}>${bancoSaldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85rem' }}>
                    <span style={{ color: '#64748b' }}>Banco / CLABE Destino:</span>
                    <strong>{selectedEmp?.banco || 'No configurado'} / {selectedEmp?.clabe || 'No configurada'}</strong>
                  </div>
                  <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 'bold' }}>
                    <span>Total a Dispersar:</span>
                    <span style={{ color: '#dc2626' }}>${selectedRecibo.netoPagado?.toFixed(2)} MXN</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                  <button
                    onClick={() => handleDownloadLayout(selectedRecibo)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                      color: '#475569'
                    }}
                  >
                    Descargar Layout
                  </button>
                  <button
                    onClick={handleConfirmDispersion}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#10b981',
                      color: 'white',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Confirmar SPEI
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
      <KondoBottomNav activeRoute="/nominas-dashboard" />
    </div>
  );
}

export default AdminTimesheetApprovalScreen;
